/**
 * One row's detail: `before`, `after`, and the four correlating identifiers.
 *
 * ── The sentence that has to be here ───────────────────────────────────────
 *
 * **`before` and `after` are metadata only** (G12) — never a secret, never private
 * content. An auditor who does not know that reads a sparse `after` as evidence that
 * a field was *cleared*, and writes it up that way. So the panel says it, above the
 * two blocks rather than beneath them.
 *
 * ── Absent is not empty, here either ───────────────────────────────────────
 *
 * `before: null` on a create and `after: null` on a delete are ordinary and mean
 * "there was no prior state" / "there is no subsequent state". A missing
 * `correlation_id` means the row was written by a path that carried none. Each of
 * those renders as **words**, never as a blank `<dd>`, for the reason the whole build
 * has one rule about: an absence that looks like a value is a value nobody can
 * challenge.
 *
 * `achau_run_id` gets a sentence of its own because it is the only field on the row
 * that reaches outside this system: the same value appears in achau's own `AuditLog`,
 * which is what makes two-sided evidence possible (02 §18).
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { AuditLogEntry } from '../../api/types';

export interface AuditDetailProps {
  readonly entry: AuditLogEntry;
}

function Block({ label, value, testId }: { label: string; value: unknown; testId: string }) {
  const { t } = useI18n();
  if (value === null || value === undefined) {
    return (
      <div className="wp-keys__item">
        <dt>{label}</dt>
        <dd className="wp-small wp-muted" data-testid={testId} data-absent="true">
          {t('k8.audit.detail.absent')}
        </dd>
      </div>
    );
  }
  return (
    <div className="wp-keys__item">
      <dt>{label}</dt>
      <dd>
        <pre className="wp-code" data-testid={testId}>
          {JSON.stringify(value, null, 2)}
        </pre>
      </dd>
    </div>
  );
}

export function AuditDetail({ entry }: AuditDetailProps): ReactNode {
  const { t } = useI18n();
  return (
    <div className="wp-audit__detail" data-testid={`k8-audit-detail-${entry.id}`}>
      <p className="wp-small wp-muted" data-testid={`k8-audit-metadata-only-${entry.id}`}>
        {t('k8.audit.detail.metadataOnly')}
      </p>
      <dl className="wp-keys wp-keys--records">
        <Block
          label={t('k8.audit.detail.before')}
          value={entry.before}
          testId={`k8-audit-before-${entry.id}`}
        />
        <Block
          label={t('k8.audit.detail.after')}
          value={entry.after}
          testId={`k8-audit-after-${entry.id}`}
        />
        <Block
          label={t('k8.audit.detail.runId')}
          value={entry.achau_run_id}
          testId={`k8-audit-runid-${entry.id}`}
        />
        <Block
          label={t('k8.audit.detail.correlationId')}
          value={entry.correlation_id}
          testId={`k8-audit-corr-${entry.id}`}
        />
        <Block
          label={t('k8.audit.detail.ip')}
          value={entry.ip_address}
          testId={`k8-audit-ip-${entry.id}`}
        />
        <Block
          label={t('k8.audit.detail.userAgent')}
          value={entry.user_agent}
          testId={`k8-audit-ua-${entry.id}`}
        />
      </dl>
      <p className="wp-small wp-muted">{t('k8.audit.detail.runIdNote')}</p>
    </div>
  );
}
